// Expert assignment + delegation hook
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from './useAuth'

export function useExpertAssignment() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const invalidateIssue = (issueId: string) => {
    queryClient.invalidateQueries({ queryKey: ['issue-detail', issueId] })
    queryClient.invalidateQueries({ queryKey: ['issue-detail', issueId, 'assignments'] })
  }

  const autoAssignMutation = useMutation({
    mutationFn: async (issueId: string) => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('Not authenticated')

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const response = await fetch(`${supabaseUrl}/functions/v1/expert-router`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ issue_id: issueId, action: 'auto_assign' }),
      })

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Auto-assign failed' }))
        throw new Error(err.error || 'Auto-assign failed')
      }
      return response.json()
    },
    onSuccess: (_, issueId) => invalidateIssue(issueId),
  })

  const manualAssignMutation = useMutation({
    mutationFn: async ({ issueId, expertId }: { issueId: string; expertId: string }) => {
      if (!user) throw new Error('Not authenticated')
      const { error } = await supabase.from('expert_assignments').insert({
        issue_id: issueId,
        expert_id: expertId,
        organization_id: user.organizationId,
        assigned_by: user.id,
        assigned_at: new Date().toISOString(),
        status: 'pending',
        priority: 'normal',
        assignment_reason: 'Manual assignment',
      } as never)
      if (error) throw error
    },
    onSuccess: (_, { issueId }) => invalidateIssue(issueId),
  })

  const reassignMutation = useMutation({
    mutationFn: async ({ assignmentId, newExpertId }: { assignmentId: string; newExpertId: string; issueId: string }) => {
      const { error } = await supabase
        .from('expert_assignments')
        .update({ expert_id: newExpertId, status: 'pending', assigned_at: new Date().toISOString() } as never)
        .eq('id', assignmentId)
      if (error) throw error
    },
    onSuccess: (_, { issueId }) => invalidateIssue(issueId),
  })

  const delegateByEmailMutation = useMutation({
    mutationFn: async ({ issueId, email, message }: { issueId: string; email: string; message: string }) => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('Not authenticated')

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const response = await fetch(`${supabaseUrl}/functions/v1/send-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: email,
          subject: `Issue delegation: ${issueId}`,
          body: message,
          issue_id: issueId,
          type: 'delegation',
        }),
      })

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Email send failed' }))
        throw new Error(err.error || 'Email send failed')
      }
      return response.json()
    },
  })

  const delegateToUserMutation = useMutation({
    mutationFn: async ({ issueId, userId }: { issueId: string; userId: string }) => {
      const { error } = await supabase
        .from('issues')
        .update({ assigned_to: userId, status: 'in_progress' } as never)
        .eq('id', issueId)
      if (error) throw error
    },
    onSuccess: (_, { issueId }) => invalidateIssue(issueId),
  })

  return {
    autoAssign: autoAssignMutation.mutateAsync,
    isAutoAssigning: autoAssignMutation.isPending,
    manualAssign: manualAssignMutation.mutateAsync,
    reassign: reassignMutation.mutateAsync,
    delegateByEmail: delegateByEmailMutation.mutateAsync,
    delegateToUser: delegateToUserMutation.mutateAsync,
    isDelegating: delegateByEmailMutation.isPending || delegateToUserMutation.isPending,
  }
}
